import { AppModalEnv } from "@faire/deprecated-retailer-visitor-shared/components/App/AppModals/types";
import { SocialLoginButton } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/__internal__/SocialLoginButton";
import { convertErrorToLoginError } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { logErrorSignIn } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/logErrorSignIn";
import { LoginError } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { LoginWrapper } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/styled";
import { View } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/View";
import { useFetchHCaptchaToken } from "@faire/deprecated-retailer-visitor-shared/hooks/hCaptcha/useFetchHCaptchaToken";
import { useHCaptchaLogin } from "@faire/deprecated-retailer-visitor-shared/hooks/hCaptcha/useHCaptchaLogin";
import { useSSOErrorStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useSSOPasswordStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOProviderOptionsStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOProviderOptionsStore";
import { useSSOStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { Core } from "@faire/design-tokens";
import userLoginData from "@faire/web-api--source/endpoints/www/api/user/login-data/post";
import resetPassword from "@faire/web-api--source/endpoints/www/api/user/reset-password/userToken/post";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILoginDataRequest } from "@faire/web-api--source/indigofair/data/ILoginDataRequest";
import { ILoginResponse } from "@faire/web-api--source/indigofair/data/ILoginResponse";
import { ISendResetPasswordRequest } from "@faire/web-api--source/indigofair/data/ISendResetPasswordRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import { emitDatadogError } from "@faire/web/common/datadog/rum";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { Button } from "@faire/web/slate-deprecated/Button";
import { GrowingSpacer, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, useCallback, useEffect, useState, type JSX } from "react";

export type HandleSSOSubmitResult = {
  nextView: View;
  user?: IUser | null;
  mfaDescription?: string;
  mfaTitle?: string;
  mfaOptions?: ILoginResponse.IMfaOption[];
  profileImage?: IImage;
  email?: string;
  userName?: string;
};

interface IProps {
  email: string;
  userToken?: string;
  onComplete: (result: HandleSSOSubmitResult) => void;
  onLogin: (user: IUser) => void;
  appModalEnv: AppModalEnv;
}

export const SSOView: FC<IProps> = ({
  email,
  userToken,
  onComplete,
  onLogin,
  appModalEnv,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [error, setError] = useState<LoginError>();
  const { fetchHCaptchaToken } = useFetchHCaptchaToken();

  const { errorMessage } = useSSOErrorStore();
  const { providers, resetProviders } = useSSOProviderOptionsStore();
  const { userHasPassword } = useSSOPasswordStore();
  const { userClickedSSOButton, setPromptSSO } = useSSOStore();

  useEffect(() => {
    if (userHasPassword) {
      logErrorSignIn("SSO", "Wrong view for user with password");
    }
  }, [userHasPassword]);

  const onFinish = useCallback(
    (result: HandleSSOSubmitResult) => {
      onComplete(result);
      resetProviders();
      setPromptSSO(false);
    },
    [onComplete, resetProviders, setPromptSSO]
  );

  // SSOView is prompted if the user has an SSO connected to their account
  // onConfirmPassword exists to confirm users password if they want to connect an SSO to their account
  // So there is no way it would be relevant here
  const onConfirmPassword = () => {};

  const onReset = async () => {
    try {
      if (userToken) {
        await resetPassword(userToken, ISendResetPasswordRequest.build({}));
      } else {
        const loginData = await fetchHCaptchaToken(true).then(() =>
          userLoginData(ILoginDataRequest.build({ email_address: email }))
        );
        const newUserToken = loginData.user_token;

        await resetPassword(
          newUserToken ?? "",
          ISendResetPasswordRequest.build({})
        );
      }
    } catch (e) {
      const error = convertErrorToLoginError(e, strictLocalize);
      emitDatadogError(error);
      setError(error);
    }
    onFinish({
      nextView: View.RESET_PASSWORD,
    });
  };

  const renderSocialLoginButton = (
    provider: SocialSignOnProvider,
    index: number
  ): React.ReactNode => (
    <SocialLoginButton
      key={index}
      provider={provider}
      onFinish={onFinish}
      onLogin={onLogin}
      onConfirmPassword={onConfirmPassword}
      sourceId="sign_in_modal_sso_view"
      appModalEnv={appModalEnv}
    />
  );

  const cumulativeErrorMessage = userClickedSSOButton
    ? errorMessage
    : error?.message;

  useHCaptchaLogin();

  const renderPasswordButton = (): React.ReactNode => {
    if (userHasPassword) {
      return null;
    }

    return (
      <Button variant="tertiary" onClick={onReset}>
        <StrictLocalMsg
          id="SkPsmn"
          defaultMessage="Reset Password"
          description={{
            text: "Call to action to reset password to newly entered password",
          }}
        />
      </Button>
    );
  };

  const Link = useCallback(
    (chunks: JSX.Element) => (
      <EmbeddedLink
        linkTo="/?signIn=1"
        onClick={() => {
          onFinish({
            nextView: View.ENTER_EMAIL,
          });
        }}
      >
        {chunks}
      </EmbeddedLink>
    ),
    [onFinish]
  );

  const LinkUsePwdCallBackFormatting = useCallback(
    (chunks: JSX.Element) => (
      <Button
        variant="tertiary"
        style={{ display: "inline" }}
        linkTo="/?signIn=1"
        onClick={() => {
          onFinish({
            nextView: View.ENTER_PASSWORD,
          });
        }}
      >
        {chunks}
      </Button>
    ),
    [onFinish]
  );

  const hasOktaProvider = providers.includes(SocialSignOnProvider.OKTA);

  return (
    <LoginWrapper>
      <GrowingSpacer />
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          id="EGtxkH"
          defaultMessage="Welcome back!"
          description={{ text: "Welcoming a returning user" }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          id="ioYkeA"
          defaultMessage="Continue as {email}"
          description={{
            text: "Continue to sign in as the person behind this email",
          }}
          values={DEPRECATED_backwardsCompatibleValues({ email })}
        />
      </Typography>
      <Typography>
        <StrictLocalMsg
          id="6ptCJH"
          defaultMessage="Not you? <Link>Choose a different account</Link>"
          values={DEPRECATED_backwardsCompatibleValues({
            Link,
          })}
        />
      </Typography>
      {hasOktaProvider && (
        <Typography>
          <StrictLocalMsg
            id="uJ/GbX"
            defaultMessage="Want to login with a password? <LinkUsePwdCallBackFormatting>Use password</LinkUsePwdCallBackFormatting>"
            values={{ LinkUsePwdCallBackFormatting }}
            description={{
              text: "On the login page when suggesting a single-sign-on option, ask the user if they would prefer to use a password instead.",
            }}
          />
        </Typography>
      )}
      {cumulativeErrorMessage ? (
        <>
          <Spacer height="2x" />
          <Typography color={Core.text.critical} role="alert" align="center">
            {cumulativeErrorMessage}
          </Typography>
          <Spacer height="3x" />
        </>
      ) : null}
      {providers.map(renderSocialLoginButton)}
      <Spacer height="2x" />
      {renderPasswordButton()}
      <GrowingSpacer />
    </LoginWrapper>
  );
};
